import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { MultiStepFormLayout } from "./components/MultiStepFormLayout";
import { QuoteUiProvider } from "./components/QuoteUiContext";
import { FullSchema } from "./utils/schema";
import { useAPI } from "../../components/lib";
import InitialInformationSkeleton from "./components/InitialInformationSkeleton";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Ban, Clock, AlertTriangle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { DEFAULTS } from "./utils/defaults";
import { mapQuoteToDefaults } from "./utils/mappers/mapQuoteToDefaults";
import { mapCompanyToDefaults } from "./utils/mappers/mapCompanyToDefaults";
import axios from "axios";

export function RunQuote() {
  const [initialValues, setInitialValues] = React.useState(DEFAULTS);
  const [loadingDefaults, setLoadingDefaults] = React.useState(true);
  const [loadedSavedQuote, setLoadedSavedQuote] = React.useState(false);
  const [initializedAccountGates, setInitializedAccountGates] = React.useState(false);

  const location = useLocation();
  const companyId = location?.state?.data?.company_id;

  const user = useAPI("/api/user");
  const producers = useAPI("/api/account");

  const [missingData, setMissingData] = React.useState(false);
  const [agencyDisabled, setAgencyDisabled] = React.useState(false);
  const [documentsPendingReview, setDocumentsPendingReview] = React.useState(false);

  const methods = useForm({
    defaultValues: React.useMemo(() => DEFAULTS, []),
    resolver: zodResolver(FullSchema),
    mode: "all",
    reValidateMode: "all",
    criteriaMode: "all",
    shouldUnregister: false,
    delayError: 120,
  });

  const hasHydratedFormRef = React.useRef(false);

  React.useEffect(() => {
    if (!producers?.data) return;

    const data = producers.data;

    if (data.documents_missing) {
      window.location.href = "/app/onboarding";
      return;
    }
    if (data.documents_pending_review) setDocumentsPendingReview(true);

    if (data.status === "partial_disabled" || data.status === "total_disabled") {
      setAgencyDisabled(true);
    }

    if (
      data.address1 === null ||
      data.address2 === null ||
      data.city === null ||
      data.state === null ||
      data.zip === null ||
      !data.certificate_contacts ||
      data.certificate_contacts.length < 1
    ) {
      setMissingData(true);
    }

    setInitializedAccountGates(true);
  }, [producers?.data]);

  React.useEffect(() => {
    let cancelled = false;

    async function initDefaults() {
      try {
        if (!producers?.data) return;
        const companyIdFromState = location.state?.data?.company_id;
        let companyData;
        const hasAddressValues = (addr = {}) => {
          const values = [
            addr.address1,
            addr.address2,
            addr.city,
            addr.state,
            addr.zip,
            addr.mailingStreet,
            addr.mailingStreet2,
            addr.mailingCity,
            addr.mailingState,
            addr.mailingZip,
            addr.garageStreet,
            addr.garageStreet2,
            addr.garageCity,
            addr.garageState,
            addr.garageZip,
          ];

          return values.some((v) => {
            if (v == null) return false;
            if (typeof v === "string") return v.trim().length > 0;
            return true;
          });
        };

        if (!location.state?.data?.quote_id && !companyIdFromState) {
          if (!cancelled) setLoadingDefaults(false);
          return;
        }

        if (companyIdFromState) {
          try {
            const companyRes = await axios.get(`/api/company/${companyIdFromState}`);
            companyData = companyRes.data.data;
          } catch (error) {
            console.error("Failed to fetch company for defaults", error);
          }
        }

        if (location.state?.data?.quote_id) {
          setLoadedSavedQuote(true);
          const res = await axios.get(`/api/quote/${location.state.data.quote_id}`);
          const companyIdForQuote =
            location.state?.data?.company_id || res?.data?.data?.company_id;

          const quoteMailing = res?.data?.data?.quote?.mailingAddress;
          const quoteGaraging = res?.data?.data?.quote?.garageAddress;
          const quoteHasAddresses =
            hasAddressValues(quoteMailing) && hasAddressValues(quoteGaraging);

          if (companyIdForQuote && !quoteHasAddresses) {
            try {
              const companyRes = await axios.get(`/api/company/${companyIdForQuote}`);
              companyData = companyRes.data.data;
            } catch (error) {
              console.error("Failed to fetch company for quote defaults", error);
            }
          }

          if (!cancelled) setInitialValues(mapQuoteToDefaults(res.data.data, producers, companyData));
        } else if (location.state?.data?.company_id) {
          if (!cancelled) setInitialValues(mapCompanyToDefaults(companyData || {}));
        }

        if (!cancelled) setLoadingDefaults(false);
      } catch (err) {
        console.error("Failed to init defaults", err);
        if (!cancelled) setLoadingDefaults(false);
      }
    }

    initDefaults();
    return () => { cancelled = true; };
  }, [location.state?.data?.company_id, location.state?.data?.quote_id, producers?.data]);

  React.useEffect(() => {
    if (hasHydratedFormRef.current) return;
    if (loadingDefaults) return;
    if (user.loading) return;
    if (producers.loading) return;
    if (!initializedAccountGates) return;

    const current = methods.getValues();
    const next = { ...current, ...initialValues };

    const currentStep1 = current.step1 ?? {};
    const initialStep1 = initialValues.step1 ?? {};
    const {
      producer: _ignoredProducer,
      allowedCarriers: _ignoredAllowedCarriers,
      ...restFromInitial
    } = initialStep1;

    next.step1 = { ...currentStep1, ...restFromInitial };

    methods.reset(next);
    if(loadedSavedQuote) methods.trigger();
    hasHydratedFormRef.current = true;
  }, [
    initialValues,
    loadingDefaults,
    loadedSavedQuote,
    user.loading,
    producers.loading,
    initializedAccountGates,
    methods,
  ]);

  const isBootstrapping =
    !hasHydratedFormRef.current ||
    loadingDefaults ||
    user.loading ||
    producers.loading ||
    !initializedAccountGates;

  if (isBootstrapping) {
    return <InitialInformationSkeleton />;
  }

  if (!companyId) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Kindly select company first</AlertTitle>
          <AlertDescription>
            Go to the quotes page and select a company first.{" "}
            <div>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => (window.location.href = "/app/quote/app")}
              >
                Go back
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (missingData) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Missing Information</AlertTitle>
          <AlertDescription>
            Your agency is missing some required data. Please fill out address
            and certificate contact information at{" "}
            <strong>Profile &gt; Company</strong>.
            <div>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => (window.location.href = "/app/account/company")}
              >
                Change
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (agencyDisabled) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert variant="destructive" className="max-w-md">
          <Ban className="h-5 w-5" />
          <AlertTitle>Agency Disabled</AlertTitle>
          <AlertDescription>
            Your agency has been disabled. If you need help, contact:{" "}
            <strong>hello@coverbadger.com</strong>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (documentsPendingReview) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert className="border-yellow-400 text-yellow-700 bg-yellow-50 max-w-md">
          <Clock className="h-5 w-5 text-yellow-600" />
          <AlertTitle>Documents Under Review</AlertTitle>
          <AlertDescription>
            Your documents are currently under review. If you need help,
            contact: <strong>hello@coverbadger.com</strong>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-(160px+3.125em))] p-6 bg-white mb-16">
      <FormProvider {...methods}>
        <QuoteUiProvider>
          <MultiStepFormLayout
            user={user}
            producers={producers}
            methods={methods}
          />
        </QuoteUiProvider>
      </FormProvider>
    </div>
  );
}
